<template>
  <div class="staff-orders">
    <h1 class="text-h5 mb-2">Staff orders</h1>
    <p class="text-body-2">See every order a staff member placed between two dates, and what was on each one.</p>

    <v-card outlined class="pa-4 mb-4">
      <v-row>
        <v-col cols="12" sm="4"><v-text-field v-model="startDate" type="date" label="Start date" outlined dense hide-details :disabled="isLoading" /></v-col>
        <v-col cols="12" sm="4"><v-text-field v-model="endDate" type="date" label="End date" outlined dense hide-details :disabled="isLoading" /></v-col>
        <v-col cols="12" sm="4">
          <v-autocomplete v-model="staffId" :items="staffOptions" item-text="label" item-value="id" label="Staff member"
            outlined dense hide-details :loading="staffLoading" :disabled="isLoading" />
        </v-col>
      </v-row>
      <p v-if="!validDates" class="error--text mt-3" role="alert">Choose valid dates, with the end date on or after the start date.</p>
      <v-alert v-if="staffError" type="error" text class="mt-3">
        {{ staffError }} <v-btn text small @click="getStaff">Retry</v-btn>
      </v-alert>
    </v-card>

    <v-btn color="primary" large :block="$vuetify.breakpoint.smAndDown" :loading="isLoading"
      :disabled="!validDates || !staffId" @click="load">Generate report</v-btn>
    <v-alert v-if="error" type="error" text class="mt-4" role="alert">{{ error }}</v-alert>

    <section v-if="generated" class="mt-6" aria-label="Staff order results" aria-live="polite">
      <h2 class="text-h6">{{ generated.staffName }}</h2>
      <p class="text-body-2">{{ generated.startDate }} to {{ generated.endDate }}</p>
      <v-alert v-if="filtersChanged" type="info" text>Dates or staff member have changed. Generate the report again to update these results.</v-alert>
      <p class="text-caption">Sales only; deleted, no sale and petty cash orders are excluded. Totals are before refunds. Orders with a single item are highlighted.</p>
      <v-row>
        <v-col v-for="stat in summary" :key="stat.label" cols="12" sm="4">
          <v-card outlined class="pa-4"><div class="text-overline">{{ stat.label }}</div><div class="text-h4">{{ stat.value }}</div></v-card>
        </v-col>
      </v-row>
      <v-checkbox v-model="singleItemOnly" label="Only show orders with 1 item" hide-details class="mb-3" />
      <v-data-table :headers="headers" :items="visibleOrders" :items-per-page="25" item-key="id" :item-class="rowClass"
        sort-by="orderPlacedAt" :mobile-breakpoint="600"
        :no-data-text="singleItemOnly ? 'No single item orders in this date range.' : 'No orders found in this date range.'">
        <template v-slot:[`item.orderPlacedAt`]="{ item }">{{ formatDate(item.orderPlacedAt) }}</template>
        <template v-slot:[`item.itemCount`]="{ item }">
          <v-chip v-if="item.itemCount === 1" small color="warning">1 item</v-chip>
          <span v-else>{{ item.itemCount }}</span>
        </template>
        <template v-slot:[`item.items`]="{ item }">
          <div v-for="(line, i) in item.items" :key="i">{{ line.quantity }} &times; {{ line.productName }}</div>
        </template>
        <template v-slot:[`item.total`]="{ item }">
          {{ money(item.total) }}
          <div v-if="item.refundedAmount > 0" class="text-caption error--text">{{ money(item.refundedAmount) }} refunded</div>
        </template>
      </v-data-table>
    </section>
  </div>
</template>

<script>
import { reportService } from '@/services';
import moment from 'moment';

export default {
  name: 'StaffOrders',
  data: () => ({
    startDate: moment().format('YYYY-MM-DD'),
    endDate: moment().format('YYYY-MM-DD'),
    staff: [], staffId: null, staffLoading: false, staffError: '',
    isLoading: false, error: '', data: [], generated: null, singleItemOnly: false,
    headers: [
      { text: 'Date', value: 'orderPlacedAt' },
      { text: 'Order ref', value: 'orderRef' },
      { text: 'Items', value: 'itemCount', align: 'right' },
      { text: 'Products', value: 'items', sortable: false },
      { text: 'Total', value: 'total', align: 'right' }
    ]
  }),
  computed: {
    staffOptions() { return this.staff.map(s => ({ id: s.id, label: s.deleted ? s.name + ' (deleted)' : s.name })); },
    validDates() {
      return moment(this.startDate, 'YYYY-MM-DD', true).isValid()
        && moment(this.endDate, 'YYYY-MM-DD', true).isValid() && this.startDate <= this.endDate;
    },
    filterKey() { return JSON.stringify([this.startDate, this.endDate, this.staffId]); },
    filtersChanged() { return this.generated && this.generated.key !== this.filterKey; },
    singleItemOrders() { return this.data.filter(o => o.itemCount === 1); },
    visibleOrders() { return this.singleItemOnly ? this.singleItemOrders : this.data; },
    summary() {
      const single = this.singleItemOrders.length;
      const share = this.data.length ? (100 * single / this.data.length).toFixed(1) : '0.0';
      return [
        { label: 'Orders', value: this.data.length },
        { label: 'Single item orders', value: `${single} (${share}%)` },
        { label: 'Total', value: this.money(this.data.reduce((sum, o) => sum + o.total, 0)) }
      ];
    }
  },
  created() { this.getStaff(); },
  methods: {
    async getStaff() {
      this.staffLoading = true;
      this.staffError = '';
      try {
        const staff = await reportService.getStaffList();
        if (!Array.isArray(staff)) throw new Error();
        this.staff = staff;
      } catch (_) {
        this.staffError = 'Unable to load staff. Please try again.';
      } finally { this.staffLoading = false; }
    },
    async load() {
      if (this.isLoading || !this.validDates || !this.staffId) return;
      const option = this.staffOptions.find(s => s.id === this.staffId);
      const snapshot = { startDate: this.startDate, endDate: this.endDate, staffName: option ? option.label : '', key: this.filterKey };
      this.isLoading = true;
      this.error = '';
      try {
        const result = await reportService.getStaffOrderReport(snapshot.startDate, snapshot.endDate, this.staffId);
        if (!Array.isArray(result)) throw new Error();
        this.data = result;
        this.generated = snapshot;
      } catch (_) {
        this.error = 'Unable to generate the report. Please try again. Any results below are from the previous report.';
      } finally { this.isLoading = false; }
    },
    rowClass(item) { return item.itemCount === 1 ? 'single-item' : ''; },
    money(value) { return new Intl.NumberFormat('en-GB', { style: 'currency', currency: 'GBP' }).format(value || 0); },
    formatDate(value) { return moment(value).format('DD/MM/YYYY HH:mm'); }
  }
};
</script>

<style scoped>
.staff-orders { max-width: 1200px; margin: 0 auto; }
.staff-orders >>> tr.single-item { background: rgba(255, 152, 0, 0.12); }
</style>
